// ─────────────────────────────────────────────────────────────────────
// computeAutoStageCandidate — pure derivation of the freeform→cascade
// auto-stage target for the dashboard composer (TaskEricInput).
//
// Extracted from TaskEricInput so the gating rules are unit-testable
// (the component only renders under jsdom, which the repo's node-env
// vitest setup doesn't provide). The component computes `intent` via
// its local parseFreeformIntent and passes the settled search results
// in; this function decides whether the cascade lift may fire.
//
// A3 regression rule (Bugs & Changes 37be58d5117d81dfad22eee2ae32e05d):
// NEVER auto-stage while a photo is staged in the composer. The lift
// deliberately discards the typed sentence (the slot strip is the
// prize) — a trade-off that's only acceptable in the pure-typing flow.
// With an attachment staged the typed text is the photo's CAPTION:
// onSubmit's staged-photo branch wins over every cascade branch and
// sends text+photo to the brain together, so lifting a cascade target
// here both wiped the caption (the draft-wipe bug) and staged a
// cascade that Send would ignore anyway.
//
// Wipe-on-detach residual (review follow-up, same ticket): clearing
// the staged photo from the AttachmentChip X lifts the gate above
// while the typed caption — and the search results already settled on
// it — are unchanged, so the lift would re-fire on the next render and
// wipe the caption the user kept to swap the photo. The host snapshots
// the composer value at clear time (detachedCaption); the lift stays
// suppressed until the text actually changes.
// ─────────────────────────────────────────────────────────────────────

import type { SearchResult, Verb } from '@/components/dashboard/agentic/types'

export type AutoStageCandidate = {
  verb: 'book' | 'reschedule'
  target: SearchResult
}

export function computeAutoStageCandidate(args: {
  /** True when composer.mode is 'idle' or 'freeform' (no cascade staged). */
  composerOpen: boolean
  dispatchPending: boolean
  /** True while a photo is staged in the composer (any classify status). */
  hasStagedAttachment: boolean
  /**
   * Composer value snapshotted when a staged photo was cleared via the
   * chip X (null when no detach is pending) — see the header comment.
   */
  detachedCaption: string | null
  /** parseFreeformIntent(value) — the host component owns the sniffer. */
  intent: Verb | null
  value: string
  broadResults: SearchResult[]
  fallbackResults: SearchResult[]
}): AutoStageCandidate | null {
  const {
    composerOpen,
    dispatchPending,
    hasStagedAttachment,
    detachedCaption,
    intent,
    value,
    broadResults,
    fallbackResults,
  } = args
  if (!composerOpen) return null
  if (dispatchPending) return null
  // A staged photo opts the whole turn out of the cascade lift (A3 — see
  // the header comment): the typed text is the photo's caption, not
  // disposable cascade filler.
  if (hasStagedAttachment) return null
  // The caption kept across a photo detach is still the photo's caption
  // (wipe-on-detach residual — see the header comment): suppress the
  // lift until the user actually edits the text.
  if (detachedCaption !== null && value === detachedCaption) return null
  if (intent !== 'book' && intent !== 'reschedule') return null
  // Require the verb followed by ≥4-char word so a partial like
  // "book ol" doesn't hijack into the cascade prematurely. The
  // schedul/reschedul stems take \w* so the real inflections
  // ("schedule", "rescheduling", …) reach the whitespace check — the
  // bare stems demanded whitespace immediately after "schedul", which
  // the full words never have, dead-ending every schedule/reschedule
  // phrasing the intent sniffer accepts. reschedul\w* stays listed
  // separately because \bschedul can't match mid-word in "reschedule".
  if (!/\b(book|schedul\w*|reschedul\w*|appointment|appt)\s+\w{4,}/i.test(value))
    return null
  // Clock-time present → user is heading for the fast-lane direct
  // brain booking. Cascade auto-stage would waste their typing.
  if (/\b\d{1,2}(:\d{2})?\s*(am|pm|a\.m|p\.m)\b/i.test(value)) return null
  // Prefer the broad search's results when it hit, otherwise fall
  // back to the name-only search results (the full-phrase ILIKE in
  // /api/search misses sentence-shaped prompts — see the fallback
  // search in TaskEricInput).
  const results = broadResults.length > 0 ? broadResults : fallbackResults
  if (results.length !== 1) return null
  const target = results[0]
  if (intent === 'book' && target.kind !== 'customer') return null
  if (intent === 'reschedule' && target.kind !== 'appointment') return null
  return { verb: intent, target }
}
